import { useState, useEffect } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { ELIGIBLE_FOR_TERMINATION_COUNT_QUERY, PEOPLE_QUERY } from "../queries";
import { Person } from "@/types";
import EmptyState from "@/components/EmptyState";
import Table, { TableFooter, TableHeading, buildActionColumn } from "@/components/Table";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import usePagination from "@/hooks/usePagination";
import { isNull } from "lodash";
import { Path, Params } from "@/router";
import PeopleTableFilters from "./PeopleTableFilters";
import MetricGrid from "@/components/MetricGrid";
import { LinkProps } from "@generouted/react-router/client";
import { PageInfo } from "@/components/Pagination";
import { useParams } from "@/hooks/useSearchParams";
import { generatePath } from "react-router-dom";
import { buildColumns } from "@/pages/billing/_utils";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { buildGraphqlInput, mapPeople, type ReportType, type Variables } from "../_utils/peopleReportMapping";
import { exportPeopleCsv, fetchAllPeopleForExport } from "../_utils/exportPeopleCsv";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";

export type { ReportType, Variables };

export interface PeopleRow {
  id: string;
  person: Person;
  pgaId?: string;
}

type StatsType = {
  totalCount: number | null;
  totalEligibleForTermination: number | null;
};

type PeopleData = {
  people: {
    nodes: Person[];
    totalCount: number;
    pageInfo: PageInfo;
  };
};

type EligibleForTerminationData = {
  availableForTerminationsDuesOrders: {
    totalCount: number;
  };
};

export const PeopleTable = <P extends Path>({
  reportType,
  defaultVariables = {}
}: {
  reportType: ReportType;
  defaultVariables?: Variables;
}) => {
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewOrders });
  const isSuspended = reportType === "suspensions";
  const isTerminations = reportType === "terminations";
  let { params, searchParamsUrl } = useParams();
  const { isCompactLayout } = useIsCompactLayout();
  const { Menu, setMenu } = useTableRowMenu();

  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();

  const normalizedParams = (() => {
    const next = { ...params } as Record<string, unknown>;
    if (typeof next.reestablished === "string") {
      if (next.reestablished === "true") next.reestablished = true;
      else if (next.reestablished === "false") next.reestablished = false;
      else delete next.reestablished;
    }
    return next;
  })();

  const [variables, setVariables] = useState<Variables>({ ...defaultVariables, ...normalizedParams });
  const [exporting, setExporting] = useState(false);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);
  const { data, loading } = useQuery<PeopleData>(PEOPLE_QUERY, {
    variables: {
      input: buildGraphqlInput(variables),
      includeSuspensions: isSuspended,
      includeTerminations: isTerminations,
      ...paginationVariables
    }
  });

  const [fetchPeoplePage] = useLazyQuery<PeopleData>(PEOPLE_QUERY, {
    fetchPolicy: "network-only"
  });

  // Do not block the people table on this expensive eligibility count.
  const {
    data: eligibleData,
    loading: eligibleLoading,
    error: eligibleError
  } = useQuery<EligibleForTerminationData>(ELIGIBLE_FOR_TERMINATION_COUNT_QUERY, { skip: !isSuspended });

  // mapPeople shapes a display row; Table columns only need the person name/photo fields.
  const peopleData = mapPeople(data?.people?.nodes || []) as unknown as PeopleRow[];

  const returnPath = `/billing/dues-management/reporting/${isSuspended ? "suspensions" : "terminations"}`;

  const [stats, setStats] = useState<StatsType>({
    totalCount: null,
    totalEligibleForTermination: null
  });

  useEffect(() => {
    if (!loading && data?.people && stats.totalCount === null) {
      setStats((prev) => ({
        ...prev,
        totalCount: data.people.totalCount
      }));
    }
  }, [loading, data, stats.totalCount]);

  useEffect(() => {
    if (!isSuspended) return;
    if (!eligibleLoading && eligibleData && stats.totalEligibleForTermination === null) {
      setStats((prev) => ({
        ...prev,
        totalEligibleForTermination: eligibleData.availableForTerminationsDuesOrders.totalCount
      }));
    }
  }, [isSuspended, eligibleLoading, eligibleData, stats.totalEligibleForTermination]);

  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { nodes, truncated } = await fetchAllPeopleForExport({
        fetchPage: async (options) => {
          const result = await fetchPeoplePage(options);
          return {
            data: result.data,
            error: result.error as Error | undefined
          };
        },
        input: buildGraphqlInput(variables),
        reportType
      });

      if (nodes.length === 0) {
        showSnackbar("No people to export for the current filters", { variant: "info" });
        return;
      }

      const count = exportPeopleCsv({ nodes, reportType, variables });
      const noun = count === 1 ? "person" : "people";
      showSnackbar(
        truncated
          ? `Exported ${count} ${noun} (page limit reached; some rows may be missing)`
          : `Exported ${count} ${noun}`
      );
    } catch {
      showErrorSnackbar("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const reestablishedFilter = variables.reestablished;
  const metricLabel = isSuspended
    ? "Total Suspended People (Unpaid Dues)"
    : reestablishedFilter === true
      ? "Total Re-established After Non-Payment"
      : reestablishedFilter === false
        ? "Total Currently Terminated (Unpaid Dues)"
        : "Total Non-Payment Terminations";

  const emptyTitle = isSuspended ? "No Suspended People from Dues Billing" : "No Non-Payment Terminations Found";
  const metricCount = data?.people?.totalCount ?? stats.totalCount;
  const eligibleCount =
    eligibleData?.availableForTerminationsDuesOrders.totalCount ?? stats.totalEligibleForTermination;
  const eligibleMetricLoading = !eligibleError && isNull(eligibleCount);
  const eligibleMetricValue = eligibleError ? "—" : `${!isNull(eligibleCount) && eligibleCount}`;
  const exportLabel = exporting ? "Exporting…" : "Export CSV";
  const heading = isSuspended ? "Dues Non-Payment Suspensions" : "Dues Non-Payment Terminations";
  const bodyMessage = isSuspended
    ? "View all persons who have been suspended due to non-payment of Dues in the current billing cycle."
    : "View Members, Associates, and Affiliates terminated for non-payment of dues. Filter by program, termination date, and re-establishment to support Canvas access removal and restore.";

  const hasActiveFilters =
    !!(variables.search || "").trim() ||
    (variables.memberClassifications?.length ?? 0) > 0 ||
    (variables.programTypes?.length ?? 0) > 0 ||
    (variables.sectionIds?.length ?? 0) > 0 ||
    (variables.facilityIds?.length ?? 0) > 0 ||
    !!variables.suspendedDateRange?.from ||
    !!variables.terminatedDateRange?.from ||
    (isTerminations && variables.reestablished !== false);

  return (
    <>
      <Menu />
      {stats.totalCount === 0 ? (
        <EmptyState title={emptyTitle} iconName="group_off" />
      ) : (
        <>
          <MetricGrid
            metrics={[
              {
                label: metricLabel,
                value: `${!isNull(metricCount) && metricCount}`,
                loading: isNull(stats.totalCount),
                iconName: "person"
              },
              ...(isSuspended
                ? [
                    {
                      label: `Total Eligible for Termination`,
                      value: eligibleMetricValue,
                      loading: eligibleMetricLoading,
                      link: { to: "/billing/dues-management/suspensions-and-terminations/terminations" } as LinkProps<
                        P,
                        Params
                      >
                    }
                  ]
                : [])
            ]}
            layout="expanded"
          />

          <TableHeading
            heading={heading}
            body={bodyMessage}
            actions={[
              {
                label: exportLabel,
                variant: "text",
                icon: "download",
                disabled: exporting || loading,
                onClick: handleExportCsv,
                testId: "export-people-csv-button"
              }
            ]}
          />
          <div className="w-full">
            <Table
              data={peopleData || []}
              loading={loading}
              hasActiveFilters={hasActiveFilters}
              getRowLink={(row) => ({
                to: generatePath(`/people/:pgaId/person/contact?return_to=${returnPath}&${searchParamsUrl}`, {
                  pgaId: row.original.pgaId!
                }) as Path
              })}
              renderNoDataEmptyState={() => <EmptyState title={emptyTitle} iconName="group_off" />}
              renderControls={(_table, { onSortClick } = {}) => (
                <PeopleTableFilters
                  reportType={reportType}
                  defaultVariables={defaultVariables}
                  variables={variables}
                  setVariables={setVariables}
                  resetPagination={resetPagination}
                  onSortClick={onSortClick}
                  pagination={createHeaderPagination(data?.people, {
                    totalCount: data?.people?.totalCount || 0,
                    pageCount: peopleData.length,
                    loading
                  })}
                />
              )}
              columns={[
                ...buildColumns<PeopleRow>({
                  isCompactLayout,
                  hasCheckbox: false
                }),
                { accessorKey: "pgaId", header: "PGA ID", enableSorting: false },
                { accessorKey: "classification", header: "Class", enableSorting: false },
                { accessorKey: "status", header: "Status", enableSorting: false },
                { accessorKey: "section", header: "Section", enableSorting: false },
                { accessorKey: "primaryFacility", header: "Primary Facility", enableSorting: false },
                ...(isSuspended
                  ? [{ accessorKey: "suspensionsDate", header: "Suspension Date", enableSorting: false }]
                  : [
                      { accessorKey: "terminationsDate", header: "Termination Date", enableSorting: false },
                      ...(isTerminations && reestablishedFilter !== false
                        ? [{ accessorKey: "reestablishedDate", header: "Re-established Date", enableSorting: false }]
                        : [])
                    ]),
                buildActionColumn<PeopleRow>({
                  onViewClick: (row) => {
                    const pgaId = row.pgaId ?? row.person?.pgaId;
                    if (pgaId) {
                      setSelectedPgaId(pgaId);
                      setIsPersonSideSheetOpen(true);
                    }
                  },
                  showViewButton: (row) => !!(row.pgaId ?? row.person?.pgaId),
                  viewButtonTitle: "View person details",
                  actionId,
                  setMenu
                })
              ]}
              renderFilteredEmptyState={() => (
                <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="person" />
              )}
              renderFooter={() => (
                <TableFooter
                  pagination={createFooterPagination(data?.people, {
                    totalCount: data?.people?.totalCount || 0,
                    pageCount: peopleData.length,
                    loading
                  })}
                />
              )}
            />
          </div>
        </>
      )}
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(undefined);
        }}
        pgaId={selectedPgaId ?? null}
        showEmployment
        showFacility
      />
    </>
  );
};

export default PeopleTable;
